/*
  The screens (BUILD-PLAN 2.11.2). One entry per route, with the module that builds it and the endpoints it reads.

  Until a feature module registers a route for a path (`src/features/<name>/routes.tsx`), the app shows a
  placeholder page for it, so every screen can be reached from the first build.

  The 2026-11-03 frame (rebuild package 1B) adds two things to the table: the view tabs of the bar (`VIEW_TABS`, with the
  `view` each screen belongs to) and the `layout` of a screen. The older screens keep their entries and are reached from the
  More menu (`NAV_GROUPS`) until each view replaces them. A screen a view has replaced leaves the table: its address is in
  `MOVED_SCREENS` and opens the view (rebuild stage 4).
*/

import { matchPath, type Params } from "react-router-dom";

import type { CommandName, QueryName } from "../api/routes";

export type ModuleId = "F01" | "F02" | "F03" | "F04" | "F05" | "F06" | "F07";

/**
 * Who may open a screen. The browser hides what a person cannot use, for comfort; the server checks
 * every request again (architecture 15.5).
 * - public: anyone, signed in or not
 * - person: any signed-in person (the admin too)
 * - workspace: a member of the active discipline, whatever the role
 * - engineer / sme: a member of the active discipline who holds that role
 * - admin: the admin
 */
export type Access = "public" | "person" | "workspace" | "engineer" | "sme" | "admin";

/**
 * The tabs of the bar. A screen with a `view` is drawn under that tab: the tab is marked while the person is on it, whatever
 * its address below the tab's own (an item page is under Items, a document under Deliverables).
 */
export type ViewId = "overview" | "items" | "shared" | "capabilities" | "deliverables" | "standards" | "review" | "files";

/**
 * How a screen sits in the frame.
 * - page: an older screen. It scrolls inside the centre region, in a padded column.
 * - view: a screen of the new design. It fills the centre region and owns its scrolling: each pane scrolls inside itself.
 */
export type Layout = "page" | "view";

export interface Screen {
  path: string;
  title: string;
  /** The module that builds the real screen. */
  module: ModuleId;
  summary: string;
  /** The queries the screen reads (names of the tables in 2.9). */
  reads: QueryName[];
  /** The commands the screen sends. */
  sends: CommandName[];
  /** Shown in the More menu of the bar, under its group. */
  nav?: string;
  access: Access;
  /** The tab this screen is under. Left out for a screen that no tab holds. */
  view?: ViewId;
  /** Left out, a screen is a `page`. */
  layout?: Layout;
}

/**
 * The groups of the More menu (CP-17). A screen with a `nav` label is in exactly one group, or stands alone (Admin). The
 * paths are listed in the order the menu shows them. A group with none visible is left out, so each role sees only what it can
 * open. A screen that a tab also opens (Library, Files) stays in its group, so every older screen is reached from More.
 */
export type NavGroupId = "explore" | "work" | "reference" | "you";

export interface NavGroup {
  id: NavGroupId;
  /** The heading of the group in the More menu. */
  label: string;
  /** What the group holds, in a sentence. */
  summary: string;
  paths: readonly string[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  { id: "explore", label: "Explore", summary: "Look at the discipline's work", paths: ["/live", "/gallery", "/whats-new"] },
  { id: "work", label: "Work", summary: "Start or change something", paths: ["/sessions/new", "/deliverables/new", "/elements/new", "/restore"] },
  { id: "reference", label: "Reference", summary: "Look something up or write it down", paths: ["/library", "/files", "/chapters", "/project-lists", "/open-points"] },
  { id: "you", label: "You", summary: "Getting started and your own settings", paths: ["/getting-started", "/profile"] },
];

/** The group a screen's address belongs to, or undefined for a screen that stands alone in the menu. */
export function groupOfPath(path: string): NavGroup | undefined {
  return NAV_GROUPS.find((group) => group.paths.includes(path));
}

/**
 * One tab of the bar. The tab opens the first of its `targets` that the person may open, and is left out when there is none.
 * Since the views of the 2026-11-03 design are built, each tab opens its own view. The older screens still under a tab (the
 * item page, the upload form, a document) stay there and in the More menu; the ones a view replaced open that view.
 */
export interface ViewTabDef {
  id: ViewId;
  /** The word on the tab. */
  label: string;
  targets: readonly string[];
}

/** The tabs of the bar, left to right (the More menu follows them). */
export const VIEW_TABS: readonly ViewTabDef[] = [
  { id: "overview", label: "Overview", targets: ["/overview"] },
  { id: "items", label: "Items", targets: ["/items"] },
  { id: "shared", label: "Shared parts", targets: ["/shared"] },
  { id: "capabilities", label: "Capabilities", targets: ["/capabilities"] },
  { id: "deliverables", label: "Deliverables", targets: ["/deliverables"] },
  { id: "standards", label: "Standards", targets: ["/library"] },
  { id: "review", label: "Review", targets: ["/review"] },
  { id: "files", label: "Files", targets: ["/files"] },
];

export const SCREENS: Screen[] = [
  // F01
  { path: "/sign-in", title: "Sign in", module: "F01", summary: "Sign in with your user name and password.", reads: ["me"], sends: ["sign_in"], access: "public" },
  { path: "/change-password", title: "Change your password", module: "F01", summary: "Pick a new password before you start.", reads: ["me"], sends: ["change_password"], access: "person" },

  // F03
  {
    path: "/overview",
    title: "Overview",
    module: "F07",
    summary: "What needs you, how far the work is and what is blocked, and who is working now, on one screen.",
    reads: ["progress", "disciplines", "home", "team", "back_lane", "item_backlog", "proposals_waiting", "inbox", "whats_new", "family_tree", "item_snapshot", "list_sessions"],
    sends: ["start_session_from_backlog", "start_session"],
    access: "workspace",
    view: "overview",
    layout: "view",
  },
  { path: "/live", title: "Live view", module: "F03", summary: "The family navigator with a replay slider.", reads: ["history_feed", "family_tree", "counters"], sends: [], nav: "Live view", access: "workspace" },
  { path: "/navigate", title: "Family navigator", module: "F03", summary: "Libraries, families and items, filtered by state and by person.", reads: ["family_tree", "history_feed", "counters"], sends: [], access: "workspace" },
  { path: "/gallery", title: "Gallery", module: "F03", summary: "One tile for each item, with a small map and a state ring.", reads: ["family_tree", "item_snapshot"], sends: [], nav: "Gallery", access: "workspace" },
  { path: "/whats-new", title: "What's new", module: "F03", summary: "What changed since your last visit, grouped by item.", reads: ["whats_new"], sends: [], nav: "What's new", access: "workspace" },
  { path: "/chapters", title: "Chapters", module: "F03", summary: "Every specification chapter, and a way to start a new one.", reads: ["list_chapters"], sends: [], nav: "Chapters", access: "workspace" },

  // F06
  {
    path: "/items",
    title: "Items",
    module: "F06",
    summary: "The libraries, the graph of one item, its attributes and its progress, and the chat, on one screen.",
    reads: ["family_tree", "find_in_ontology", "item_page", "item_snapshot", "disciplines"],
    sends: [],
    access: "workspace",
    view: "items",
    layout: "view",
  },
  {
    path: "/shared",
    title: "Shared parts",
    module: "F07",
    summary: "The parts, shared fields, data groups and pick lists the items share, what a part holds and who decides on it.",
    reads: ["shelf", "project_lists", "element", "part_kind_view", "element_graph", "element_impact", "disciplines", "list_sessions", "session", "session_cards", "conversation"],
    sends: ["start_session", "resume_session", "post_message", "accept_card", "change_accept_card", "decline_card"],
    access: "workspace",
    view: "shared",
    layout: "view",
  },
  {
    path: "/capabilities",
    title: "Capabilities",
    module: "F07",
    summary: "The capabilities loaded with the app and the team's own, with the attributes of the one chosen.",
    reads: ["capabilities", "capability_view", "progress", "element", "element_graph", "element_impact", "card", "session", "session_cards", "conversation"],
    sends: ["set_capability_step", "start_session", "resume_session", "post_message", "accept_card", "change_accept_card", "decline_card"],
    access: "workspace",
    view: "capabilities",
    layout: "view",
  },

  // F02
  { path: "/sessions/new", title: "New session", module: "F02", summary: "Pick an item and say what you want to do.", reads: ["item_backlog", "list_elements"], sends: ["start_session"], nav: "New session", access: "engineer" },
  { path: "/sessions/:id", title: "Session", module: "F02", summary: "The trail, the live map, the plan, the proposals and the agent.", reads: ["session", "item_snapshot", "session_cards", "restore_points"], sends: ["accept_card", "decline_card", "plan_go", "pause_session", "finish_session"], access: "workspace" },
  { path: "/sessions/:id/log", title: "Session log", module: "F02", summary: "The reasons, decisions and questions of this session.", reads: ["session_log"], sends: [], access: "workspace" },
  { path: "/sessions/:id/checks", title: "Checks list", module: "F02", summary: "Errors first, then warnings, then notes.", reads: ["checks_list"], sends: ["run_checks"], access: "workspace" },
  { path: "/sessions/:id/coverage/:reportId", title: "Coverage report", module: "F02", summary: "How much of a document is covered, and what is left.", reads: ["coverage_report"], sends: ["run_coverage"], access: "workspace" },
  { path: "/sessions/:id/hand-overs/:hoId", title: "Hand-over", module: "F02", summary: "What goes to the SME, what is held back, and the questions.", reads: ["hand_over"], sends: ["edit_hand_over", "send_hand_over"], access: "workspace" },
  { path: "/items/:id/map", title: "Live map of an item", module: "F02", summary: "The item, its components, tie-in points and stubs.", reads: ["item_snapshot"], sends: [], access: "workspace" , view: "items" },
  { path: "/restore", title: "Restore control", module: "F02", summary: "Go back to an earlier point, with a preview first.", reads: ["restore_points", "preview_restore"], sends: ["restore", "make_restore_point"], nav: "Going back", access: "engineer" },

  // F04
  { path: "/items/:id", title: "Item page", module: "F04", summary: "Everything about one item: where it is drawn, its fields, components and library mapping.", reads: ["item_page"], sends: ["edit_element", "withdraw_element"], access: "workspace" , view: "items" },
  { path: "/elements/new", title: "Add an element by hand", module: "F04", summary: "A form for each kind of element.", reads: ["list_elements", "symbol_set"], sends: ["add_by_hand"], nav: "Add an element", access: "engineer" , view: "items" },
  { path: "/elements/:id", title: "Element", module: "F04", summary: "One element with its lines, notes and history.", reads: ["element"], sends: ["add_line", "add_note"], access: "workspace" , view: "items" },
  { path: "/elements/:id/edit", title: "Edit an element", module: "F04", summary: "Change an element in place or as a Next version.", reads: ["element"], sends: ["edit_element", "take_lock", "release_lock"], access: "engineer" , view: "items" },
  { path: "/symbols", title: "Symbol picker", module: "F04", summary: "Browse the symbol library, place connection points, upload a drawing, and pick the symbol for each kind of drawing.", reads: ["symbol_set"], sends: ["pick_symbol", "upload_symbol", "convert_symbol", "change_connection_point", "set_symbol_points"], access: "workspace" },
  {
    path: "/deliverables",
    title: "Deliverables",
    module: "F07",
    summary: "The deliverables on and off the target list, the lines of one, a sample document and the progress.",
    reads: ["deliverable_kinds", "deliverable_lines", "progress", "list_deliverables", "deliverable", "spots", "element", "family_tree", "list_elements", "team", "conversation"],
    sends: ["set_target_list", "post_message"],
    access: "workspace",
    view: "deliverables",
    layout: "view",
  },
  { path: "/deliverables/new", title: "Upload a document", module: "F04", summary: "Add a datasheet, drawing or list for the agent to read.", reads: ["list_deliverables"], sends: ["upload_deliverable"], nav: "Add a document", access: "engineer" , view: "deliverables" },
  { path: "/deliverables/:id", title: "Document view", module: "F04", summary: "The pages, the spots read from them, and what could not be read.", reads: ["deliverable", "spots"], sends: ["start_intake", "confirm_intake", "cancel_intake", "correct_spot"], access: "workspace" , view: "deliverables" },
  { path: "/library", title: "Library browser", module: "F04", summary: "Search the stored standards and make a mapping line.", reads: ["library_list", "library_search", "library_term"], sends: ["add_line"], nav: "Library", access: "workspace" , view: "standards" },
  { path: "/files", title: "Files view", module: "F04", summary: "The files the app writes, with a preview of each.", reads: ["files_view", "file_preview"], sends: ["request_files_write"], nav: "Files", access: "workspace" , view: "files" },
  { path: "/chapters/:key", title: "Chapter editor", module: "F04", summary: "Write and settle the sections of a specification chapter.", reads: ["chapter", "list_chapters"], sends: ["edit_chapter_section"], access: "workspace" },
  { path: "/project-lists", title: "Project code lists", module: "F04", summary: "Load the codes your project uses.", reads: ["project_lists"], sends: ["load_project_list"], nav: "Project lists", access: "workspace" },
  { path: "/open-points", title: "Open points", module: "F04", summary: "Questions that are still open.", reads: ["open_points"], sends: ["log_open_point", "close_open_point", "reopen_open_point"], nav: "Open points", access: "workspace" },

  // F05
  {
    path: "/review",
    title: "Review",
    module: "F07",
    summary: "The expert's inbox, the element in context, the review card and what came back, on one screen.",
    reads: ["inbox", "sme_card", "card", "back_lane", "disciplines", "element_graph", "deliverable", "conversation"],
    sends: ["display_card", "validate", "validate_line", "send_back", "reject", "reject_line", "add_note", "repropose", "post_message"],
    access: "workspace",
    view: "review",
    layout: "view",
  },
  {
    path: "/getting-started",
    title: "Getting started",
    module: "F07",
    summary: "A short presentation of the app for your role, with a chat to ask anytime and the common questions.",
    reads: ["onboarding", "onboarding_slides", "onboarding_faq", "admin_memory", "conversation"],
    sends: ["set_slide_position", "start_practice", "set_active_workspace", "set_module_status", "edit_admin_memory", "post_message", "post_admin_message"],
    nav: "Getting started",
    access: "person",
    layout: "view",
  },
  { path: "/profile", title: "My profile", module: "F05", summary: "What the agent remembers about you, and your own model key.", reads: ["my_memory"], sends: ["edit_my_memory", "clear_my_memory", "set_personal_key"], nav: "My profile", access: "person" },
  { path: "/admin", title: "Admin", module: "F05", summary: "Accounts, the model connection and the disciplines.", reads: ["admin_people", "model_connection", "admin_workspaces"], sends: ["create_account", "set_model_connection", "test_model_connection", "add_workspace"], nav: "Admin", access: "admin" },
];

/**
 * An older address that a view of the 2026-11-03 design has replaced (rebuild stage 4). It is no screen any more: the router
 * sends it to the view, so a bookmark, a link in a toast or a pointer of the agent still lands. What the older address named
 * goes into the view's own parameter, and the rest of the query (the discipline of the bar, `?d=`) is kept.
 */
export interface MovedScreen {
  /** The older address, as the router matches it. */
  from: string;
  /** The view that replaced it. */
  to: string;
  /** The parameter of the older address that names what the view opens, and the view's own name for it. */
  keep?: { param: string; as: string };
}

export const MOVED_SCREENS: readonly MovedScreen[] = [
  // The home composition is retired. The admin, who has no discipline, still starts on the Admin page (AccessOutlet).
  { from: "/", to: "/overview" },
  { from: "/inbox", to: "/review" },
  { from: "/inbox/:elementId", to: "/review", keep: { param: "elementId", as: "card" } },
  { from: "/back-from-sme", to: "/review" },
  { from: "/onboarding", to: "/getting-started" },
];

/** A parameter of an address as it reads, so it is not escaped twice when it goes into the query. */
function plain(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function movedTarget(moved: MovedScreen, params: Params, search: string): string {
  const query = new URLSearchParams(search);
  const value = moved.keep ? params[moved.keep.param] : undefined;
  if (moved.keep && value) query.set(moved.keep.as, plain(value));
  const text = query.toString();
  return text ? `${moved.to}?${text}` : moved.to;
}

/**
 * The address a moved one opens, or null when the address has not moved. What the older address named goes into the view's
 * own parameter; every other part of the query is kept as it was.
 */
export function movedPath(pathname: string, search = ""): string | null {
  for (const moved of MOVED_SCREENS) {
    const match = matchPath({ path: moved.from, end: true }, pathname);
    if (match) return movedTarget(moved, match.params, search);
  }
  return null;
}

/** The path of a screen with its parameters filled in, for links and tests. */
export function pathFor(screen: Screen, params: Record<string, string> = {}): string {
  return screen.path.replace(/:(\w+)/g, (_all, key: string) => params[key] ?? `example-${key}`);
}

/** The screen an address belongs to, or undefined (the page-not-found page). */
export function findScreen(pathname: string): Screen | undefined {
  return SCREENS.find((screen) => matchPath({ path: screen.path, end: true }, pathname));
}
